import React, { useContext } from "react";
import compose from "compose-function";
import moment from "moment";

import TableComponent from "../../components/TableComponent";
import CreateItemButton from "../../components/CreateItemButton";

import { useColumns } from "../../hooks/useColumns";
import useUserPermissionsContext from "../../hooks/useUserPermissionsContext";

import {
  getSortOrder,
  sortColumns,
  sortModelToString,
  fetchDataWithOptionsAndFilters,
  exportToFile,
} from "../../utils/utils";

import {
  addHrefToRow,
  changeDeletedToBoolean,
  addValueFormatter,
  padValueWith,
  readDataFromLocalStorage,
  getDefaultFilterModel,
  getInitialFilters,
  generateEntityKeyForLocalStorage,
  addFormatters,
} from "../../utils/table";

import { hardDelete, softDelete } from "../../dao/operations";

import { editRecords, exportRecords } from "../../dao/common";
import { FILTER_TYPES } from "../../dao/types";
import { SECTION_RELATIONAL_FIELDS } from "../../constants/softDelete";

import { getDefaultSort } from "../../constants/defaultSorting";

import UserRights from "../../enums/UserRights";

import { ROUTES } from "../../enums/Routes";
import { API_ROUTES } from "../../enums/api";
import { addSearchParams } from "../../utils/dao";

import { GlobalContext } from "../../globalContext";
import useEntities from "../../hooks/useEntities";
import { useSortModel } from "../../hooks/useSortModel";

const API_ROUTE = API_ROUTES.sections;
/**
 * Here we need to transform the row data retrieved,
 * in order to make it compatible with @mui/x-data-grid-pro
 */
const transformRows = (rows) => {
  return rows.reduce((acc, row) => {
    return [
      ...acc,
      compose(
        changeDeletedToBoolean("deleted"),
        addHrefToRow({
          field: "id",
          slug: "section",
        })
      )(row),
    ];
  }, []);
};

/**
 * Here we need to transform the column data retrieved,
 * in order to add @mui/x-data-grid-pro formatters
 */
const transformColumns = (columns) => {
  return addFormatters([
    { field: "system_group_number", formatter: addValueFormatter("system_group_number")(padValueWith(3)) },
    { field: "number", formatter: addValueFormatter("number")(padValueWith(2)) },
    { field: "system_number", formatter: addValueFormatter("system_number")(padValueWith(3)) },
    { field: "object_number", formatter: addValueFormatter("object_number")(padValueWith(5)) },
  ], columns);
};

export default function SectionsContainer({
  nestedEntity = false,
  parent = {},
}) {
  const { globalFilters } = useContext(GlobalContext);

  // Get cached local filter model from local storage
  // with a fallback to default field values
  const cachedLocalFilter = readDataFromLocalStorage(
    generateEntityKeyForLocalStorage(FILTER_TYPES.section, "filterModel", {
      nestedEntity,
    })
  ) ?? {
    filterModel: null,
    filterJSON: "",
  };

  // destructure fields from cachedLocalFilter
  const {
    filterModel: cachedLocalFilterModel,
    filterJSON: cachedFilterJSONString,
  } = cachedLocalFilter;

  const initialFilters = getInitialFilters({
    cache: {
      localFilterModel: cachedLocalFilterModel,
      filterJSONString: cachedFilterJSONString,
    },
    defaultFilterModel: getDefaultFilterModel("object_number").filterModel,
  });

  const { columns } = useColumns(FILTER_TYPES.section);

  // Context-aware default sorting using centralized function
  const defaultSort = getDefaultSort(FILTER_TYPES.section, { parent });

  const { sortModel, setSortModel } = useSortModel(FILTER_TYPES.section, {
    nestedEntity,
    parent,
    columns,
  });
  const sortModelString = sortModelToString(sortModel, columns) || defaultSort;

  const {
    data,
    loadingState,
    fetchEntitiesData: fetchSectionsData,
    setFilterString,
    setOffset,
    limit,
    offset,
    setLimit,
    totalNumberOfRecords,
  } = useEntities("sections", {
    params: {
      object_ids: parent?.object_id ?? globalFilters?.value ?? null,
      equipment_ids: parent?.equipment_id ?? null,
    },
    initialFilters: initialFilters.apiFilterString,
    order: sortModelString,
  });

  const fetchRecordsDataWithGlobalFilters = fetchDataWithOptionsAndFilters(
    fetchSectionsData
  )({
    limit,
    offset,
    order: sortModelString,
    params: {
      object_ids: parent?.object_id ?? globalFilters?.value ?? null,
      equipment_ids: parent?.equipment_id ?? null,
    },
  });

  const fetchExportData = async (filterString, selectedColumnsString) => {
    const abortController = new AbortController();
    const searchParams = {
      filters: filterString,
      columns: selectedColumnsString,
      object_ids: parent?.object_id ?? globalFilters?.value ?? null,
      equipment_ids: parent?.equipment_id ?? null,
    };
    const response = await exportRecords(
      API_ROUTE,
      searchParams,
      abortController
    );

    exportToFile(
      `export_${FILTER_TYPES.section}_overview_${moment().format(
        "DD-MM-YYYY"
      )}.csv`,
      response
    );

    return "Export done";
  };

  const { isAllowedTo } = useUserPermissionsContext();

  const isAllowedToCreate = isAllowedTo(
    UserRights.CREATE,
    FILTER_TYPES.section
  );
  const isAllowedToDelete = isAllowedTo(
    UserRights.SOFT_DELETE,
    FILTER_TYPES.section
  );
  const isAllowedToExport = isAllowedTo(UserRights.EXPORT_CSV);
  const isAllowedToMassEdit = isAllowedTo(UserRights.MASS_EDIT_SECTION);
  const isAllowedToInlineEdit = isAllowedTo(UserRights.INLINE_EDIT_SECTION);

  const tableOptions = {
    hasDelete: isAllowedToDelete,
    sortOrder: getSortOrder(FILTER_TYPES.section),
    hasMassEdit: isAllowedToMassEdit,
    hasInlineEdit: isAllowedToInlineEdit,
  };

  const searchParams = new URLSearchParams(
    Object.entries({
      objectId: parent?.object_id || globalFilters?.value,
      systemGroupId: parent.system_group_id, // defined when sub overview
      systemId: parent.system_id, // defined when sub overview
      sectionId: parent.section_id, // defined when sub overview
      locationId: parent.location_id, // defined when sub overview
      equipmentId: parent.equipment_id, // defined when sub overview
    }).filter(([, value]) => value)
  ).toString();

  return (
    <>
      <TableComponent
        {...{
          columns: transformColumns(sortColumns(columns)),
          transformColumns,
          loading: loadingState,
          rows: transformRows(data),
          refreshData: fetchRecordsDataWithGlobalFilters,
          renderCreateEntityButton: () => (
            <CreateItemButton
              to={addSearchParams(ROUTES.section.create)(searchParams)}
              isDisabled={!isAllowedToCreate}
              entity={FILTER_TYPES.section}
            />
          ),
          editRecords,
          softDeleteEntry: softDelete({
            route: API_ROUTE,
            delete: true,
            includeFields: SECTION_RELATIONAL_FIELDS,
          }),
          hardDeleteEntry: hardDelete({
            route: API_ROUTE,
          }),
          restoreEntry: softDelete({
            route: API_ROUTE,
            delete: false,
            includeFields: SECTION_RELATIONAL_FIELDS,
          }),
          tableOptions,
          setFilters: setFilterString,
          setOffset,
          offset,
          setLimit,
          sortModel,
          setSortModel,
          numberOfRecords: totalNumberOfRecords,
          isAllowedToExport,
          entity: FILTER_TYPES.section,
          nestedEntity: nestedEntity,
          initialFilterModel: initialFilters.grid,
          exportRecords: fetchExportData,
        }}
      />
    </>
  );
}
